import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { GameState } from '@sheshdash/shared';
import { getPlayerColorHex } from '../../utils/skinHelpers';
import { LanguageSwitcher } from '../LanguageSwitcher';

interface WaitingRoomProps {
  gameState: GameState;
  playerId: string | null;
  isConnected: boolean;
  error: string | null;
  isPrivate: boolean;
  onTogglePrivacy: () => void;
  onStartGame: () => void;
  onAddBots: (count: number) => void;
  onLeaveGame: () => void;
  onShowRules: () => void;
  onShowUserAgreement: () => void;
}

export function WaitingRoom({
  gameState, playerId, isConnected, error,
  isPrivate, onTogglePrivacy,
  onStartGame, onAddBots, onLeaveGame, onShowRules, onShowUserAgreement,
}: WaitingRoomProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const currentPlayers = gameState.players.length;
  const canStartGame = currentPlayers >= 2;
  const maxBots = 4 - currentPlayers;
  const creatorId = gameState.players[0]?.id ?? null;
  const isCreator = Boolean(playerId && creatorId && playerId === creatorId);

  const copyId = () => {
    navigator.clipboard.writeText(gameState.id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="waiting-room w-full max-w-md mx-auto mt-12 px-4 flex flex-col items-center gap-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <div className="w-14 h-14 bg-slate-900 dark:bg-slate-800 rounded-2xl shadow-xl flex items-center justify-center border-2 border-slate-800 dark:border-slate-700">
          <svg className="w-7 h-7 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        </div>
        <div>
          <h1 className="text-3xl font-black text-slate-800 dark:text-slate-100 tracking-tighter leading-none">SHESHDASH</h1>
          <span className="text-[9px] font-black text-indigo-500 tracking-widest uppercase">Fast Moves. Smart Strategy.</span>
        </div>
      </div>

      {/* Game ID */}
      <div className="w-full bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-3 flex items-center justify-between shadow-sm" data-testid="game-id" data-game-id={gameState.id}>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold text-slate-400 dark:text-slate-400 uppercase">{t('game.gameId')}:</span>
          <code className="text-sm font-black text-slate-700 dark:text-slate-200">{gameState.id.substring(0, 8)}</code>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="waiting-toggle-privacy"
            onClick={onTogglePrivacy}
            disabled={!isCreator}
            className={`px-2.5 py-1.5 text-xs font-bold rounded-lg transition-all ${
              isPrivate ? 'bg-slate-800 dark:bg-slate-100 text-white dark:text-slate-900' : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700'
            }`}
            title={isCreator
              ? (isPrivate ? t('game.privateGameEnabled', 'Private game enabled') : t('game.privateGameDisabled', 'Public game'))
              : t('game.onlyCreatorCanManageWaitingRoom', 'Only the creator can manage the lobby')}
          >
            {isPrivate ? '🔒' : '🔓'}
          </button>
          <button
            data-testid="waiting-copy-id"
            onClick={copyId}
            className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${
              copied ? 'bg-emerald-500 text-white dark:text-slate-900' : 'bg-indigo-500 text-white dark:text-slate-900 hover:bg-indigo-600 dark:hover:bg-indigo-400'
            }`}
          >
            {copied ? '\u2713' : t('game.copyId')}
          </button>
        </div>
      </div>

      {/* Connection status */}
      <p
        data-testid="waiting-connection-status"
        data-connection={isConnected ? 'connected' : 'connecting'}
        className={`text-sm font-bold ${isConnected ? 'text-emerald-500' : 'text-amber-500'}`}
      >
        {isConnected ? t('game.connected') : t('game.connecting')}
      </p>

      {error && (
        <div className="w-full bg-rose-50 dark:bg-rose-950 border border-rose-200 text-rose-700 dark:text-rose-300 rounded-xl p-3 text-sm font-bold">
          {error}
        </div>
      )}

      {/* Main card */}
      <div className="w-full bg-white dark:bg-slate-900 rounded-[2rem] shadow-sm border border-slate-100 dark:border-slate-800 p-6 flex flex-col gap-4">
        <h2 className="text-lg font-black text-slate-800 dark:text-slate-100 text-center">{t('game.waitingForPlayers')}</h2>
        <p className="text-center text-slate-500 dark:text-slate-400 font-bold">{currentPlayers} / 2-4 {t('game.players')}</p>

        {/* Player list */}
        <div className="space-y-2">
          {gameState.players.map(player => (
            <div key={player.id} className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center shadow-sm"
                style={{ backgroundColor: getPlayerColorHex(player) }}
              >
                <span className="text-white dark:text-slate-900 font-black text-sm">{player.name.charAt(0)}</span>
              </div>
              <span className="font-bold text-slate-800 dark:text-slate-100 text-sm" style={{ color: getPlayerColorHex(player) }}>
                {player.name}
              </span>
              {player.id === creatorId && (
                <span className="text-xs bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 px-2 py-0.5 rounded font-bold">
                  {t('lobby.creator', 'Creator')}
                </span>
              )}
              {player.is_bot && <span className="text-xs bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded font-bold">Bot</span>}
            </div>
          ))}
        </div>

        {isCreator && maxBots > 0 && (
          <div className="flex flex-col gap-2">
            <p className="text-xs font-bold text-slate-400 dark:text-slate-400 uppercase tracking-wide text-center">
              {t('bots.addTitle')}
            </p>
            {[...Array(maxBots)].map((_, i) => {
              const count = i + 1;
              return (
                <button
                  key={count}
                  data-tutorial="add-bots"
                  data-testid={`waiting-add-bots-${count}`}
                  onClick={() => onAddBots(count)}
                  disabled={!isConnected}
                  className="w-full py-2.5 bg-gradient-to-r from-[#6366f1] to-[#a855f7] text-white dark:text-slate-900 font-black rounded-xl shadow hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {count > 1 ? t('bots.addButtonPlural', { count }) : t('bots.addButton', { count })}
                </button>
              );
            })}
          </div>
        )}

        {canStartGame && isCreator && (
          <button
            data-tutorial="start-game"
            data-testid="waiting-start-game"
            onClick={onStartGame}
            disabled={!isConnected}
            className="w-full py-3 bg-emerald-500 text-white dark:text-slate-900 font-black rounded-xl shadow-lg hover:bg-emerald-600 dark:hover:bg-emerald-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('game.startGame')}
          </button>
        )}

        {!canStartGame && (
          <p className="text-center text-rose-500 text-sm font-bold">{t('game.needMorePlayers')}</p>
        )}

        {canStartGame && !isCreator && (
          <p className="text-center text-slate-500 dark:text-slate-400 text-sm font-bold">
            {t('game.onlyCreatorCanStart', 'Only the creator can start the game.')}
          </p>
        )}

        <div className="text-center text-xs text-slate-400 dark:text-slate-400 space-y-1">
          <p>{t('game.shareGameId')}</p>
          <p>{t('game.canStartWithPlayers')}</p>
        </div>

        <button
          data-testid="waiting-leave-game"
          onClick={onLeaveGame}
          className="w-full py-3 bg-rose-500 text-white dark:text-slate-900 font-black rounded-xl hover:bg-rose-600 dark:hover:bg-rose-400 transition-all"
        >
          {t('game.exitGame')}
        </button>
      </div>

      <LanguageSwitcher />

      <div className="flex gap-4 items-center">
        <button data-testid="waiting-show-rules" onClick={onShowRules} className="text-xs text-indigo-500 font-bold underline">{t('rules.showRules')}</button>
        <button data-testid="waiting-show-user-agreement" onClick={onShowUserAgreement} className="text-xs text-slate-400 dark:text-slate-400 underline opacity-60 hover:opacity-100">{t('userAgreement.title')}</button>
      </div>

      <div className="h-8" />
    </div>
  );
}
